import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthProviderKind } from '@ciimar/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useBranding } from '../branding/BrandingContext';
import { useT } from '../lib/i18n';

export function Login() {
  const { login, passwordLogin } = useAuth();
  const branding = useBranding();
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';
  const urlError = new URLSearchParams(location.search).get('error');

  const [provider, setProvider] = useState<AuthProviderKind | null>(null);
  const [localLogin, setLocalLogin] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .authConfig()
      .then((c) => {
        setProvider(c.provider);
        setLocalLogin(c.localLogin);
      })
      .catch(() => {
        // If the config can't be loaded, fall back to showing both options
        // (Microsoft + local form) instead of silently hiding local login.
        setProvider(AuthProviderKind.Entra);
        setLocalLogin(true);
        setError(t('auth.login.configLoadError'));
      });
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await passwordLogin(username, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('auth.login.signInFailed'));
    } finally {
      setBusy(false);
    }
  }

  const pageStyle = branding?.loginBackground
    ? {
        backgroundImage: `linear-gradient(rgba(10,20,40,0.45), rgba(10,20,40,0.45)), url(${branding.loginBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <div className="public-page" style={pageStyle}>
      <div className="card narrow">
        {branding?.logo ? (
          <img
            className="login-logo"
            src={branding.logo}
            alt={branding.name ?? t('auth.login.logoAlt')}
          />
        ) : (
          <h1>
            {branding?.name ?? 'CIIMAR'} <span className="muted">Portal</span>
          </h1>
        )}
        <p className="muted">{t('auth.login.subtitle')}</p>

        {urlError && <div className="alert error">{t('auth.login.urlError')}</div>}
        {error && <div className="alert error">{error}</div>}

        {provider === null ? (
          <p className="muted">{t('common.loading')}</p>
        ) : (
          <>
            {provider === AuthProviderKind.Entra && (
              <button type="button" onClick={() => login(from)}>
                {t('auth.login.entraButton')}
              </button>
            )}

            {(provider !== AuthProviderKind.Entra || localLogin) && (
              <>
                {provider === AuthProviderKind.Entra && localLogin && (
                  <div className="divider">
                    <span>{t('auth.login.localDivider')}</span>
                  </div>
                )}
                <form onSubmit={onSubmit}>
                  <label>
                    {t('auth.login.username')}
                    <input
                      required
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      autoFocus={provider !== AuthProviderKind.Entra}
                    />
                  </label>
                  <label>
                    {t('auth.login.password')}
                    <input
                      required
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                  </label>
                  <button type="submit" disabled={busy}>
                    {busy ? t('auth.login.signingIn') : t('auth.login.signIn')}
                  </button>
                </form>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
